import React, { useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { Typography, IconButton, Dialog, DialogContent, Tooltip } from "@mui/material";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../Firebase";
import {
    kProjectsCollectionName,
    kTracksCollectionName,
    ProjectDeleted,
    TrackProjectId,
    TrackDeleted,
    WorkspaceId,
} from "../../utils/CollectionConstants";
import { AuthContext } from "../../context/AuthContext";
import { useAssetStore } from "../../storage/AssetStore";
import { useConfirmDialog } from "./DeleteConfirmation";
import {
    formatDurationBrief,
    normalizeTimestampToMillis,
    formatToHumanTime,
    IsVideoTrackType,
    formatToReadableLargeTitle,
} from "../../utils/utils";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import { ClickAwayListener, Card } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ListViewThumbnailCard } from "./ProjectThumbnail";
import { trackSessionEvent } from "../../analytics/sessionEvents";
import {useNavigate} from "react-router-dom";

// Cutoff date: January 1, 2026 00:00:00 UTC
const LEGACY_CUTOFF_TIMESTAMP = new Date("2026-01-01T00:00:00Z").getTime();

const openProjectSidebarFilesAfterNavigation = () => {
    if (typeof window === "undefined") return;

    const dispatch = () => {
        window.dispatchEvent(new CustomEvent("openProjectSidebarFiles", { detail: { source: "legacyProjectUpgrade" } }));
    };

    // Route transitions can be racy; fire a couple times so V2Editor has a chance to attach its listener.
    window.setTimeout(dispatch, 0);
    window.setTimeout(dispatch, 100);
    window.setTimeout(dispatch, 300);
};

// ─────────────────────────────────────────────────────────────────────────────
// Styles (following DesignSystem.txt)
// ─────────────────────────────────────────────────────────────────────────────

const headerTextStyle = {
    fontFamily: "Inter",
    fontSize: 26,
    fontWeight: 600,
    lineHeight: "32px",
    letterSpacing: "-0.2px",
    color: "var(--app-text-primary)",
};

const sectionSubtitleStyle = {
    fontFamily: "Inter",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "20px",
    color: "var(--app-text-muted)",
    marginTop: "6px",
    marginBottom: "4px",
};

const listContainerStyle = {
    marginTop: 8,
};

const legacyListHeaderStyle = {
    display: "flex",
    alignItems: "center",
    padding: "8px 20px",
    borderBottom: "1px solid var(--app-border-subtle)",
    background: "transparent",
    gap: 2,
    marginBottom: 8,
};

const legacyListHeaderTextStyle = {
    fontFamily: "Inter",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--app-text-muted)",
};

const legacyRowStyle = {
    display: "flex",
    height: "60px",
    padding: "10px 20px",
    alignItems: "center",
    alignSelf: "stretch",
    borderRadius: "8px",
    background: "transparent",
    transition: "background 0.12s ease",
    "&:hover": {
        background: "rgba(43, 107, 253, 0.04)",
        cursor: "pointer",
    },
    "&:hover .legacy-upgrade-btn": {
        opacity: 1,
        pointerEvents: "auto",
    },
};

const legacyThumbnailAreaStyle = {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flex: "1 1 50%",
    maxWidth: "50%",
};

const legacyThumbnailContainerStyle = {
    width: "72px",
    height: "40px",
    borderRadius: "6px",
    flexShrink: 0,
};

const legacyTitleStyle = {
    color: "var(--app-text-primary)",
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "18px",
    letterSpacing: "-0.1px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
};

const legacyCellTextStyle = {
    fontFamily: "Inter",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "16px",
    letterSpacing: "0.01em",
    color: "var(--app-text-muted)",
};

const backButtonStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    cursor: "pointer",
    color: "#2B6BFD",
    fontFamily: "Inter",
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 8,
    "&:hover": {
        textDecoration: "underline",
    },
};

const emptyStateStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 24px",
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
    fontSize: 14,
};

// Context menu styles
const contextMenuStyle = {
    position: "absolute",
    display: "flex",
    padding: "8px",
    flexDirection: "column",
    gap: "2px",
    borderRadius: "10px",
    background: "var(--app-bg-surface)",
    border: "1px solid var(--app-border-subtle)",
    boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.08)",
    zIndex: 1000,
    minWidth: "160px",
};

const menuItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
    cursor: "pointer",
    borderRadius: "8px",
    transition: "background 0.12s ease",
    "&:hover": {
        background: "var(--app-bg-subtle)",
    },
};

const menuItemTextStyle = {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    color: "var(--app-text-primary)",
};

const deleteMenuItemStyle = {
    ...menuItemStyle,
    "&:hover": {
        background: "rgba(237, 12, 50, 0.06)",
    },
};

const deleteMenuItemTextStyle = {
    ...menuItemTextStyle,
    color: "#C63636",
};

// ─────────────────────────────────────────────────────────────────────────────
// Track Context Menu
// ─────────────────────────────────────────────────────────────────────────────

const TrackContextMenu = ({ anchorPosition, onClose, onDownload, onDelete }) => {
    return (
        <ClickAwayListener onClickAway={onClose}>
            <Card
                sx={{
                    ...contextMenuStyle,
                    top: anchorPosition.top,
                    left: anchorPosition.left,
                }}
            >
                <Box sx={menuItemStyle} onClick={onDownload}>
                    <DownloadIcon sx={{ fontSize: 18, color: "var(--app-icon-primary)" }} />
                    <Box sx={menuItemTextStyle}>Download</Box>
                </Box>
                <Box sx={deleteMenuItemStyle} onClick={onDelete}>
                    <DeleteOutlinedIcon sx={{ fontSize: 18, color: "#C63636" }} />
                    <Box sx={deleteMenuItemTextStyle}>Delete</Box>
                </Box>
            </Card>
        </ClickAwayListener>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Track Preview Modal
// ─────────────────────────────────────────────────────────────────────────────

const TrackPreviewModal = ({ open, onClose, track, trackUrl }) => {
    if (!track || !trackUrl) return null;

    const isVideo = IsVideoTrackType(track.trackType);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    overflow: "hidden",
                    maxHeight: "85vh",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 20px",
                    borderBottom: "1px solid var(--app-border-subtle)",
                }}
            >
                <Typography
                    sx={{
                        fontFamily: "Inter",
                        fontSize: 16,
                        fontWeight: 500,
                        color: "var(--app-text-primary)",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                    }}
                >
                    {track.title || "Untitled"}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>
            <DialogContent
                sx={{
                    padding: "24px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "var(--app-bg-canvas)",
                    minHeight: "300px",
                }}
            >
                {/* Video element handles both video and audio files */}
                <video
                    src={trackUrl}
                    controls
                    autoPlay={false}
                    onPlay={() => trackSessionEvent("played_legacy_project_file")}
                    style={{
                        maxWidth: "100%",
                        maxHeight: isVideo ? "60vh" : "80px",
                        borderRadius: "8px",
                        backgroundColor: isVideo ? "#000" : "transparent",
                    }}
                >
                    Your browser does not support the video/audio element.
                </video>
            </DialogContent>
        </Dialog>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Legacy Project Detail (tracks list for a single project)
// ─────────────────────────────────────────────────────────────────────────────

const LegacyProjectDetail = ({ project, onBack }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const authContext = useContext(AuthContext);
    const workspaceId = authContext.getCurrentWorkspaceId?.();
    const { DeleteTrack, GetTrackUrl, DeleteProject, BringLegacyProjectToNewLibretto } = useAssetStore();
    const navigate = useNavigate();
    const { openDialog, ConfirmDialogComponent } = useConfirmDialog({
        confirmText: "Delete",
        cancelText: "Cancel",
    });

    const [tracks, setTracks] = useState([]);
    const [contextMenu, setContextMenu] = useState(null);
    const [previewModal, setPreviewModal] = useState({ open: false, track: null, url: null });
    const [loadingUrl, setLoadingUrl] = useState(false);
    const [isBringing, setIsBringing] = useState(false);

    // Subscribe to tracks for this project
    useEffect(() => {
        if (!project?.projectId || !workspaceId) return;

        const q = query(
            collection(db, kTracksCollectionName),
            where(TrackProjectId, "==", project.projectId),
            where(WorkspaceId, "==", workspaceId),
            where(TrackDeleted, "==", false)
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const newTracks = [];
                snapshot.forEach((doc) => {
                    const data = doc.data();
                    // Only include ready tracks (case-insensitive)
                    if (data.status && data.status.toLowerCase() === "ready") {
                        newTracks.push(data);
                    }
                });
                // Sort by createTime descending
                newTracks.sort((a, b) => {
                    const aTime = normalizeTimestampToMillis(a.createTime) || 0;
                    const bTime = normalizeTimestampToMillis(b.createTime) || 0;
                    return bTime - aTime;
                });
                setTracks(newTracks);
            },
            (error) => {
                console.error("Error fetching legacy tracks:", error);
            }
        );

        return () => unsubscribe();
    }, [project?.projectId, workspaceId]);

    const handleContextMenuClick = (e, track) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setContextMenu({
            track,
            position: {
                top: rect.bottom + 4,
                left: rect.left - 120,
            },
        });
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const handleDownload = async () => {
        if (!contextMenu?.track) return;
        const track = contextMenu.track;
        handleCloseContextMenu();

        try {
            const url = await GetTrackUrl({ trackId: track.trackId });
            if (url) {
                trackSessionEvent("downloaded_legacy_project_file");
                // Open in new tab - browser will handle download for media files
                window.open(url, "_blank");
            }
        } catch (error) {
            console.error("Error getting track URL for download:", error);
        }
    };

    const handleDeleteTrack = () => {
        if (!contextMenu?.track) return;
        const track = contextMenu.track;
        handleCloseContextMenu();

        openDialog({
            title: "Delete Track",
            message: `"${track.title || "Untitled"}" will be permanently deleted.`,
            onConfirm: async () => {
                trackSessionEvent("deleted_legacy_project_file");
                await DeleteTrack({ trackId: track.trackId });
            },
        });
    };

    const handleDeleteProject = () => {
        openDialog({
            title: "Delete Project",
            message: `"${project.name || "Untitled Project"}" and all its tracks will be permanently deleted.`,
            onConfirm: async () => {
                await DeleteProject({ projectId: project.projectId });
                onBack();
            },
        });
    };

    const handleBringToNewLibretto = (event) => {
        event.stopPropagation();
        if (isBringing) return;

        openDialog({
            title: "Bring to the new Libretto",
            message: `This will reprocess this project's files so they can be used in the new Libretto.`,
            confirmText: "Bring",
            cancelText: "Cancel",
            onConfirm: async () => {
                try {
                    setIsBringing(true);
                    trackSessionEvent("brought_legacy_project");
                    // Navigate immediately so the user can see the project + tracks in a processing state.
                    navigate(`/editor/${project.projectId}`);
                    openProjectSidebarFilesAfterNavigation();
                    void BringLegacyProjectToNewLibretto({ projectId: project.projectId }).catch((e) => {
                        console.error("Error bringing legacy project to new Libretto:", e);
                    });
                } catch (e) {
                    console.error("Error bringing legacy project to new Libretto:", e);
                } finally {
                    setIsBringing(false);
                }
            },
        });
    };

    const handleTrackClick = async (track) => {
        setLoadingUrl(true);
        try {
            const url = await GetTrackUrl({ trackId: track.trackId });
            if (url) {
                setPreviewModal({ open: true, track, url });
            }
        } catch (error) {
            console.error("Error getting track URL for preview:", error);
        } finally {
            setLoadingUrl(false);
        }
    };

    const handleClosePreview = () => {
        setPreviewModal({ open: false, track: null, url: null });
    };

    return (
        <Box>
            <ConfirmDialogComponent />
            
            {/* Back button */}
            <Box sx={backButtonStyle} onClick={onBack}>
                <KeyboardArrowLeftIcon sx={{ fontSize: 20 }} />
                <span>Back to Legacy Projects</span>
            </Box>

            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Box>
                    <Typography sx={headerTextStyle}>{project.name || "Untitled Project"}</Typography>
                    <Typography sx={sectionSubtitleStyle}>
                        {tracks.length} {tracks.length === 1 ? "track" : "tracks"} •{" "}
                        Created {formatToHumanTime(project.createTime)}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip title="Bring to the new Libretto">
                        <IconButton
                            onClick={handleBringToNewLibretto}
                            sx={{
                                color: "#2B6BFD",
                                "&:hover": { backgroundColor: "rgba(43, 107, 253, 0.08)" },
                            }}
                        >
                            <DriveFileMoveOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        onClick={handleDeleteProject}
                        sx={{
                            color: "#C63636",
                            "&:hover": { backgroundColor: "rgba(237, 12, 50, 0.06)" },
                        }}
                    >
                        <DeleteOutlinedIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Tracks list */}
            <Box sx={listContainerStyle}>
                {tracks.length > 0 ? (
                    <>
                        {/* Header row */}
                        <Box sx={legacyListHeaderStyle}>
                            <Box sx={{ flex: "6 1 0", minWidth: 0, display: "flex", alignItems: "center", gap: "16px" }}>
                                <Box sx={{ width: "72px", height: "1px" }} />
                                <Box sx={legacyListHeaderTextStyle}>Name</Box>
                            </Box>
                            <Box sx={{ flex: "2 1 0", minWidth: 0, ...legacyListHeaderTextStyle }}>Duration</Box>
                            <Box sx={{ flex: "2 1 0", minWidth: 0, ...legacyListHeaderTextStyle }}>Created</Box>
                            <Box sx={{ flex: "0 0 40px" }} />
                        </Box>

                        {/* Track rows */}
                        {tracks.map((track) => (
                            <Box
                                key={track.trackId}
                                sx={legacyRowStyle}
                                onClick={() => handleTrackClick(track)}
                            >
                                <Box
                                    sx={{
                                        flex: "6 1 0",
                                        minWidth: 0,
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box sx={legacyThumbnailAreaStyle}>
                                        <Box sx={legacyThumbnailContainerStyle}>
                                            <ListViewThumbnailCard
                                                thumbnailId={null}
                                                title={track.title || "Untitled"}
                                                projectId={project.projectId}
                                                isVideo={IsVideoTrackType(track.trackType)}
                                                onClick={() => handleTrackClick(track)}
                                            />
                                        </Box>
                                        <Box sx={legacyTitleStyle}>
                                            {formatToReadableLargeTitle(track.title || "Untitled")}
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ flex: "2 1 0", minWidth: 0, display: "flex", alignItems: "center" }}>
                                    <Box sx={legacyCellTextStyle}>
                                        {track.duration ? formatDurationBrief(track.duration) : "—"}
                                    </Box>
                                </Box>
                                <Box sx={{ flex: "2 1 0", minWidth: 0, display: "flex", alignItems: "center" }}>
                                    <Box sx={legacyCellTextStyle}>
                                        {track.createTime ? formatToHumanTime(track.createTime) : "—"}
                                    </Box>
                                </Box>
                                <Box sx={{ flex: "0 0 40px", display: "flex", justifyContent: "center" }}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleContextMenuClick(e, track)}
                                        sx={{
                                            opacity: 0.6,
                                            transition: "opacity 0.15s ease",
                                            "&:hover": { opacity: 1, backgroundColor: "var(--app-bg-subtle)" },
                                        }}
                                    >
                                        <MoreVertIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </>
                ) : (
                    <Box sx={emptyStateStyle}>
                        <FolderOpenOutlinedIcon sx={{ fontSize: 40, color: "var(--app-border-muted)", mb: 2 }} />
                        <Typography>No tracks found in this project</Typography>
                    </Box>
                )}
            </Box>

            {/* Context menu */}
            {contextMenu && (
                <TrackContextMenu
                    anchorPosition={contextMenu.position}
                    onClose={handleCloseContextMenu}
                    onDownload={handleDownload}
                    onDelete={handleDeleteTrack}
                />
            )}

            {/* Preview modal */}
            <TrackPreviewModal
                open={previewModal.open}
                onClose={handleClosePreview}
                track={previewModal.track}
                trackUrl={previewModal.url}
            />

            {/* Loading overlay for URL fetch */}
            {loadingUrl && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: isDarkMode ? "rgba(18, 20, 24, 0.55)" : "rgba(255,255,255,0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                    }}
                >
                    <Typography sx={{ fontFamily: "Inter", color: "var(--app-text-muted)" }}>
                        Loading...
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Legacy Projects View (main component)
// ─────────────────────────────────────────────────────────────────────────────

export const LegacyProjectsView = () => {
    const authContext = useContext(AuthContext);
    const workspaceId = authContext.getCurrentWorkspaceId?.();

    const [legacyProjects, setLegacyProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [trackCounts, setTrackCounts] = useState({});
    const { openDialog, ConfirmDialogComponent } = useConfirmDialog({});
    const [isBringing, setIsBringing] = useState(false);

    const navigate = useNavigate();

    const navigateToProject = (projectId) => {
        navigate(`/editor/${projectId}`);
    }

    const {BringLegacyProjectToNewLibretto} = useAssetStore();

    const handleBringToNewLibretto = ({event, projectId}) => {
        event.stopPropagation();
        if (isBringing) return;

        openDialog({
            title: "Bring to the new Libretto",
            message: `This will reprocess this project's files so they can be used in the new Libretto.`,
            confirmText: "Bring",
            cancelText: "Cancel",
            onConfirm: async () => {
                try {
                    setIsBringing(true);
                    trackSessionEvent("brought_legacy_project");
                    // Navigate immediately so the user can see the project + tracks in a processing state.
                    navigateToProject(projectId);
                    openProjectSidebarFilesAfterNavigation();
                    void BringLegacyProjectToNewLibretto({ projectId: projectId }).catch((e) => {
                        console.error("Error bringing legacy project to new Libretto:", e);
                    });

                } catch (e) {
                    console.error("Error bringing legacy project to new Libretto:", e);
                } finally {
                    setIsBringing(false);
                }
            },
        });
    };

    // Track when users open the legacy projects view.
    useEffect(() => {
        trackSessionEvent("viewed_legacy_project");
    }, []);

    // Subscribe to legacy projects (createTime < 2026)
    useEffect(() => {
        if (!workspaceId) return;

        const q = query(
            collection(db, kProjectsCollectionName),
            where(WorkspaceId, "==", workspaceId),
            where(ProjectDeleted, "==", false)
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const projects = [];
                snapshot.forEach((doc) => {
                    const data = doc.data();
                    const createTime = normalizeTimestampToMillis(data.createTime);
                    // Filter for projects created before 2026
                    if (createTime && createTime < LEGACY_CUTOFF_TIMESTAMP) {
                        projects.push(data);
                    }
                });
                // Sort by createTime descending
                projects.sort((a, b) => {
                    const aTime = normalizeTimestampToMillis(a.createTime) || 0;
                    const bTime = normalizeTimestampToMillis(b.createTime) || 0;
                    return bTime - aTime;
                });
                setLegacyProjects(projects);
            },
            (error) => {
                console.error("Error fetching legacy projects:", error);
            }
        );

        return () => unsubscribe();
    }, [workspaceId]);

    // Fetch track counts for each project
    useEffect(() => {
        if (!workspaceId || legacyProjects.length === 0) return;

        const unsubscribes = [];

        legacyProjects.forEach((project) => {
            const q = query(
                collection(db, kTracksCollectionName),
                where(TrackProjectId, "==", project.projectId),
                where(WorkspaceId, "==", workspaceId),
                where(TrackDeleted, "==", false)
            );

            const unsub = onSnapshot(q, (snapshot) => {
                let count = 0;
                snapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data.status && data.status.toLowerCase() === "ready") {
                        count++;
                    }
                });
                setTrackCounts((prev) => ({ ...prev, [project.projectId]: count }));
            });

            unsubscribes.push(unsub);
        });

        return () => {
            unsubscribes.forEach((unsub) => unsub());
        };
    }, [workspaceId, legacyProjects]);

    const handleProjectClick = (project) => {
        setSelectedProject(project);
    };

    const handleUpgradeClick = (event) => {
        event.stopPropagation();
    };

    const handleBack = () => {
        setSelectedProject(null);
    };

    // Filter to only show projects that have at least 1 track
    const projectsWithTracks = legacyProjects.filter(
        (project) => (trackCounts[project.projectId] ?? 0) > 0
    );

    // If a project is selected, show its detail view
    if (selectedProject) {
        return <LegacyProjectDetail project={selectedProject} onBack={handleBack} />;
    }

    // Main projects list view
    return (
        <Box>
            <ConfirmDialogComponent />
            <Typography sx={headerTextStyle}>Legacy Projects</Typography>
            <Typography sx={sectionSubtitleStyle}>
                Projects from the old Libretto. Download the files or bring the project to the new Libretto to use it here.
            </Typography>

            <Box sx={{ marginTop: 8 }}>
                {projectsWithTracks.length > 0 ? (
                    <>
                        {/* Header row */}
                        <Box sx={legacyListHeaderStyle}>
                            <Box sx={{ flex: "6 1 0", minWidth: 0, display: "flex", alignItems: "center", gap: "16px" }}>
                                <Box sx={{ width: "72px", height: "1px" }} />
                                <Box sx={legacyListHeaderTextStyle}>Name</Box>
                            </Box>
                            <Box sx={{ flex: "2 1 0", minWidth: 0, ...legacyListHeaderTextStyle }}>Files</Box>
                            <Box sx={{ flex: "2 1 0", minWidth: 0, ...legacyListHeaderTextStyle }}>Created</Box>
                            <Box sx={{ flex: "0 0 40px" }} />
                        </Box>

                        {/* Project rows */}
                        {projectsWithTracks.map((project) => (
                            <Box
                                key={project.projectId}
                                sx={legacyRowStyle}
                                onClick={() => handleProjectClick(project)}
                            >
                                <Box
                                    sx={{
                                        flex: "6 1 0",
                                        minWidth: 0,
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box sx={legacyThumbnailAreaStyle}>
                                        <Box sx={legacyThumbnailContainerStyle}>
                                            <ListViewThumbnailCard
                                                thumbnailId={project.thumbnailId}
                                                title={project.name}
                                                projectId={project.projectId}
                                                isVideo={!!project.thumbnailId}
                                                onClick={() => handleProjectClick(project)}
                                            />
                                        </Box>
                                        <Box sx={legacyTitleStyle}>
                                            {formatToReadableLargeTitle(project.name)}
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ flex: "2 1 0", minWidth: 0, display: "flex", alignItems: "center" }}>
                                    <Box sx={legacyCellTextStyle}>
                                        {trackCounts[project.projectId] === 1
                                            ? "1 file"
                                            : `${trackCounts[project.projectId] ?? 0} files`}
                                    </Box>
                                </Box>
                                <Box sx={{ flex: "2 1 0", minWidth: 0, display: "flex", alignItems: "center" }}>
                                    <Box sx={legacyCellTextStyle}>
                                        {project.createTime ? formatToHumanTime(project.createTime) : "—"}
                                    </Box>
                                </Box>
                                <Box
                                    className="legacy-upgrade-btn"
                                    sx={{
                                        flex: "0 0 40px",
                                        display: "flex",
                                        justifyContent: "center",
                                        opacity: 0,
                                        pointerEvents: "none",
                                        transition: "opacity 120ms ease",
                                    }}
                                >
                                    <Tooltip title="Bring to the new Libretto">
                                        <IconButton
                                            size="small"
                                            onClick={(event) => handleBringToNewLibretto({event, projectId: project.projectId})}
                                            sx={{
                                                color: "#2B6BFD",
                                                "&:hover": { backgroundColor: "rgba(43, 107, 253, 0.08)" },
                                            }}
                                        >
                                            <DriveFileMoveOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        ))}
                    </>
                ) : (
                    <Box sx={emptyStateStyle}>
                        <FolderOpenOutlinedIcon sx={{ fontSize: 40, color: "var(--app-border-muted)", mb: 2 }} />
                        <Typography>No legacy projects found</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default LegacyProjectsView;
